/**
 * Lead Flow Schema v2
 * Stored in LeadFlow.schema (draft) and LeadFlow.publishedSchema (live).
 * Compiled to SurveyJS JSON at render time.
 */

import type { SkinScanMetricKey } from './skin-scan-types.js';

/** Public storefront payload from GET /lead-flows/slug/:slug */
export type PublishedLeadFlow = {
  id: string;
  name: string;
  slug: string;
  schema: unknown;
  version: number;
};

export type FlowStepType =
  | 'radiogroup'
  | 'checkbox'
  | 'text'
  | 'rating'
  | 'imagepicker'
  | 'html'
  | 'skin-scan'
  | 'lead-gate'
  | 'end';

export interface FlowChoice {
  value: string;
  /** Primary line (e.g. skin type name). */
  label: string;
  /** Secondary line under the label — no em dash in the same string. */
  description?: string;
  /** Optional illustration shown beside the question column when this choice is selected. */
  imageUrl?: string;
}

export interface FlowBranchRule {
  skipTo: string;
}

interface FlowStepBase {
  id: string;
  type: FlowStepType;
  title: string;
  description?: string;
  required?: boolean;
  orderWeight?: number;
}

export interface FlowStepRadiogroup extends FlowStepBase {
  type: 'radiogroup';
  choices: FlowChoice[];
  branching?: Record<string, FlowBranchRule>;
}

export interface FlowStepCheckbox extends FlowStepBase {
  type: 'checkbox';
  choices: FlowChoice[];
  /** SurveyJS maxSelectedChoices — e.g. top 3 concerns. */
  maxSelected?: number;
}

export interface FlowStepText extends FlowStepBase {
  type: 'text';
  placeholder?: string;
  inputType?: 'text' | 'email' | 'phone' | 'number' | 'textarea';
}

export interface FlowStepRating extends FlowStepBase {
  type: 'rating';
  rateMin?: number;
  rateMax?: number;
  minRateDescription?: string;
  maxRateDescription?: string;
}

export interface FlowStepImagePicker extends FlowStepBase {
  type: 'imagepicker';
  choices: FlowChoice[];
  multiSelect?: boolean;
}

export interface FlowStepHtml extends FlowStepBase {
  type: 'html';
  htmlContent: string;
}

export interface FlowStepSkinScan extends FlowStepBase {
  type: 'skin-scan';
  mode?: 'mock' | 'live';
  captureDurationMs?: number;
  showMetrics?: SkinScanMetricKey[];
  disclaimerHtml?: string;
}

export interface FlowStepLeadGate extends FlowStepBase {
  type: 'lead-gate';
  fields: ('email' | 'phone' | 'firstName' | 'lastName' | 'name' | 'company')[];
  emailRequired?: boolean;
  phoneRequired?: boolean;
  consentText?: string;
  enrichment?: {
    enabled: boolean;
    skipFieldsIfKnown?: string[];
  };
}

export interface FlowStepEnd extends FlowStepBase {
  type: 'end';
  actionType: 'routine-match' | 'redirect' | 'thank-you';
  redirectUrl?: string;
  thankYouMessage?: string;
}

export type FlowStep =
  | FlowStepRadiogroup
  | FlowStepCheckbox
  | FlowStepText
  | FlowStepRating
  | FlowStepImagePicker
  | FlowStepHtml
  | FlowStepSkinScan
  | FlowStepLeadGate
  | FlowStepEnd;

export interface FlowSchemaMeta {
  title: string;
  description?: string;
  actionType?: string;
  thankYouMessage?: string;
}

export interface FlowSchema {
  version: 2;
  meta: FlowSchemaMeta;
  steps: FlowStep[];
}

export const STEP_TYPE_WEIGHTS: Record<FlowStepType, number> = {
  html: 10,
  radiogroup: 20,
  checkbox: 20,
  rating: 30,
  imagepicker: 30,
  text: 40,
  'skin-scan': 15,
  'lead-gate': 80,
  end: 100,
};

export const STEP_TYPE_LABELS: Record<FlowStepType, string> = {
  radiogroup: 'Choix unique',
  checkbox: 'Choix multiple',
  text: 'Texte libre',
  rating: 'Notation',
  imagepicker: "Sélecteur d'images",
  html: 'Contenu / Info',
  'skin-scan': 'Analyse peau (caméra)',
  'lead-gate': 'Capture de contact',
  end: 'Fin du flow',
};

export const STEP_TYPE_ICONS: Record<FlowStepType, string> = {
  radiogroup: '○',
  checkbox: '☑',
  text: '✎',
  rating: '★',
  imagepicker: '🖼',
  html: 'ℹ',
  'skin-scan': '📷',
  'lead-gate': '🔒',
  end: '✓',
};

export function createDefaultStep(type: FlowStepType): FlowStep {
  const base = {
    id: `step-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    title: '',
    orderWeight: STEP_TYPE_WEIGHTS[type],
  };

  switch (type) {
    case 'radiogroup':
      return {
        ...base,
        type,
        required: true,
        choices: [
          { value: 'option-1', label: 'Option 1' },
          { value: 'option-2', label: 'Option 2' },
          { value: 'option-3', label: 'Option 3' },
        ],
      };
    case 'checkbox':
      return {
        ...base,
        type,
        choices: [
          { value: 'option-1', label: 'Option 1' },
          { value: 'option-2', label: 'Option 2' },
        ],
      };
    case 'text':
      return { ...base, type, inputType: 'text', placeholder: '' };
    case 'rating':
      return { ...base, type, rateMin: 1, rateMax: 5 };
    case 'imagepicker':
      return { ...base, type, choices: [], multiSelect: false };
    case 'html':
      return { ...base, type, htmlContent: '<p>Contenu informatif</p>' };
    case 'skin-scan':
      return {
        ...base,
        type,
        title: 'Analyse de votre peau',
        required: true,
        mode: 'mock',
        captureDurationMs: 3500,
        showMetrics: ['hydration', 'texture', 'spots'],
        disclaimerHtml:
          '<p>Cet outil fournit des indications cosmétiques uniquement, sans valeur médicale. Les images ne sont pas enregistrées.</p>',
      };
    case 'lead-gate':
      return {
        ...base,
        type,
        title: 'Vos coordonnées',
        fields: ['email', 'phone'],
        emailRequired: true,
        consentText: "J'accepte d'être contacté(e)",
      };
    case 'end':
      return {
        ...base,
        type,
        title: 'Merci !',
        actionType: 'thank-you',
        thankYouMessage: 'Nous vous contacterons bientôt.',
      };
  }
}

export function createEmptyFlowSchema(): FlowSchema {
  return {
    version: 2,
    meta: { title: 'Nouveau Flow' },
    steps: [createDefaultStep('lead-gate'), createDefaultStep('end')],
  };
}

/** Default regimen builder — Ordinary-inspired steps mapped to routine-match tags. */
export function createRegimenBuilderFlowSchema(): FlowSchema {
  return {
    version: 2,
    meta: {
      title: 'Construire ma routine',
      description:
        'Type de peau, préoccupations et rythme — une routine matin / soir indicative.',
    },
    steps: [
      {
        id: 'skinType',
        type: 'radiogroup',
        title: 'Quel type de peau\u00a0?',
        required: true,
        orderWeight: 20,
        choices: [
          {
            value: 'normale',
            label: 'Normale',
            description: 'confortable, ni trop grasse ni trop sèche',
            imageUrl: '/assets/lead-flow/skin-type/normale.webp',
          },
          {
            value: 'seche',
            label: 'Sèche',
            description: 'tiraillements, manque de confort',
            imageUrl: '/assets/lead-flow/skin-type/seche.webp',
          },
          {
            value: 'grasse',
            label: 'Grasse',
            description: 'brillance, pores visibles',
            imageUrl: '/assets/lead-flow/skin-type/grasse.webp',
          },
          {
            value: 'mixte',
            label: 'Mixte',
            description: 'zone T plus grasse, joues plus sèches',
            imageUrl: '/assets/lead-flow/skin-type/mixte.webp',
          },
          {
            value: 'sensible',
            label: 'Sensible',
            description: 'rougeurs ou réactions faciles',
            imageUrl: '/assets/lead-flow/skin-type/sensible.webp',
          },
        ],
      },
      {
        id: 'concern',
        type: 'checkbox',
        title: 'Quelles sont vos préoccupations principales\u00a0?',
        description: 'Jusqu’à 3 choix. Le premier compte le plus pour la routine proposée.',
        required: true,
        orderWeight: 20,
        maxSelected: 3,
        choices: [
          {
            value: 'hydratation',
            label: 'Hydratation',
            description: 'déshydratation, soif de confort',
            imageUrl: '/assets/lead-flow/preoccupations/hydration.webp',
          },
          {
            value: 'antiage',
            label: 'Anti-âge',
            description: 'fermeté, ridules',
            imageUrl: '/assets/lead-flow/preoccupations/anti-aging.webp',
          },
          {
            value: 'imperfections',
            label: 'Imperfections',
            description: 'congestion, boutons',
            imageUrl: '/assets/lead-flow/preoccupations/imperfections.webp',
          },
          {
            value: 'teint',
            label: 'Éclat',
            description: 'teint irrégulier',
            imageUrl: '/assets/lead-flow/preoccupations/irregulier.webp',
          },
          {
            value: 'protection',
            label: 'Protection',
            description: 'SPF, barrière',
            imageUrl: '/assets/lead-flow/preoccupations/protection.webp',
          },
          {
            value: 'sensibilite',
            label: 'Sensibilité',
            description: 'rougeurs, réactions',
          },
        ],
      },
      {
        id: 'pace',
        type: 'radiogroup',
        title: 'Combien de produits dans votre routine\u00a0?',
        description: 'Simple = express ; plus d’étapes = routine complète.',
        required: true,
        orderWeight: 20,
        choices: [
          { value: 'simple', label: 'Simple (3 produits)' },
          { value: 'essentials', label: 'Essentiels (4–5 produits)' },
          { value: 'advanced', label: 'Avancée (6+ produits)' },
        ],
      },
      {
        id: 'texture',
        type: 'radiogroup',
        title: 'Quelle texture préférez-vous\u00a0?',
        required: true,
        orderWeight: 20,
        choices: [
          { value: 'legere', label: 'Légère (gel, fluide)' },
          { value: 'confort', label: 'Confort (crème)' },
          { value: 'sans-parfum', label: 'Sans parfum' },
        ],
      },
      {
        id: 'lead-gate',
        type: 'lead-gate',
        title: 'Recevez votre routine personnalisée',
        orderWeight: 80,
        fields: ['email', 'phone'],
        emailRequired: false,
        phoneRequired: false,
        consentText: "J'accepte d'être contacté(e) par Hamssah",
      },
      {
        id: 'end',
        type: 'end',
        title: 'Merci !',
        orderWeight: 100,
        actionType: 'routine-match',
        thankYouMessage: 'Votre routine personnalisée est prête.',
      },
    ],
  };
}

/** Skin analyzer flow: camera scan (mock) → contact → routine match. */
export function createSkinAnalyzerFlowSchema(): FlowSchema {
  return {
    version: 2,
    meta: {
      title: 'Analyse de peau IA',
      description:
        'Scannez votre visage à la caméra pour une première analyse cosmétique et une routine adaptée.',
    },
    steps: [
      {
        id: 'skinScan',
        type: 'skin-scan',
        title: 'Scannez votre visage',
        description:
          'Placez votre visage dans le cadre. L’analyse est indicative et ne remplace pas un avis médical.',
        required: true,
        orderWeight: 15,
        mode: 'live',
        captureDurationMs: 3500,
        showMetrics: ['hydration', 'texture', 'spots', 'redness'],
        disclaimerHtml:
          '<p>En activant la caméra, vous acceptez un traitement local de l’image dans votre navigateur (aucun envoi serveur en phase prototype). Indications cosmétiques uniquement.</p>',
      },
      {
        id: 'lead-gate',
        type: 'lead-gate',
        title: 'Recevez votre analyse détaillée',
        orderWeight: 80,
        fields: ['email', 'phone'],
        emailRequired: false,
        phoneRequired: false,
        consentText: "J'accepte d'être contacté(e) par Hamssah pour des conseils personnalisés",
      },
      {
        id: 'end',
        type: 'end',
        title: 'Merci !',
        orderWeight: 100,
        actionType: 'routine-match',
        thankYouMessage: 'Votre routine personnalisée est prête.',
      },
    ],
  };
}
